// Pure helpers of the PV screens (COMX-FE-04): the draft as the editor holds it, its conversion to
// the PATCH body, the order of the items, and the answers of the server.
import type {
  MeetingMinutesDtoOutput,
  MeetingMinutesDtoOutputItemsItemKind,
  UpdateMinutesDto,
} from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import { clockOf, dayOf, instantOf } from '@/features/calendar/model';

export type Minutes = MeetingMinutesDtoOutput;
export type ItemKind = MeetingMinutesDtoOutputItemsItemKind;
export type Template = Minutes['template'];
export type MinutesLanguage = Minutes['language'];

export const ITEM_KINDS: readonly ItemKind[] = ['point', 'decision', 'action'];
export const TEMPLATES: readonly Template[] = ['coordination', 'moa', 'site_meeting', 'site_visit'];
export const LANGUAGES: readonly MinutesLanguage[] = ['fr', 'ar', 'en'];
export const TITLE_MAX = 200;
export const LOCATION_MAX = 200;
export const NAME_MAX = 120;
export const ITEM_TEXT_MAX = 2000;
export const ATTENDEES_MAX = 100;
export const ITEMS_MAX = 200;

/** Values of the `dir` attribute (named, so that the i18n lint sees no literal text). */
export const DIR_AUTO = 'auto';
export const DIR_LTR = 'ltr';
export const TASKS_TAB = 'tasks';

export const etagOf = (version: number): string => `W/"${String(version)}"`;

export interface DraftAttendee {
  userId: string | null;
  name: string;
  organization: string | null;
  present: boolean;
}

export interface DraftItem {
  id: string;
  kind: ItemKind;
  text: string;
  assigneeId: string | null;
  assigneeName: string | null;
  dueDate: string | null;
  taskId: string | null;
  carriedFrom: string | null;
}

/** What the editor holds: the PV's fields, the date and the time apart (Africa/Tunis). */
export interface Draft {
  title: string;
  template: Template;
  language: MinutesLanguage;
  day: string;
  clock: string;
  location: string;
  attendees: DraftAttendee[];
  items: DraftItem[];
}

export function draftOf(minutes: Minutes): Draft {
  return {
    title: minutes.title,
    template: minutes.template,
    language: minutes.language,
    day: dayOf(minutes.heldAt),
    clock: clockOf(minutes.heldAt),
    location: minutes.location ?? '',
    attendees: minutes.attendees.map((a) => ({ ...a })),
    items: minutes.items.map((item) => ({ ...item })),
  };
}

export const isDirty = (a: Draft, b: Draft): boolean => JSON.stringify(a) !== JSON.stringify(b);

const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

export type DraftProblem =
  { field: 'title' | 'day' | 'clock' } | { field: 'attendee' | 'item'; index: number };

/** What would make the server refuse the draft (so the editor says it before asking). */
export function problemsOf(draft: Draft): DraftProblem[] {
  const problems: DraftProblem[] = [];
  if (draft.title.trim() === '') problems.push({ field: 'title' });
  if (!DAY.test(draft.day)) problems.push({ field: 'day' });
  if (!CLOCK.test(draft.clock)) problems.push({ field: 'clock' });
  draft.attendees.forEach((a, index) => {
    if (a.name.trim() === '') problems.push({ field: 'attendee', index });
  });
  draft.items.forEach((item, index) => {
    if (item.text.trim() === '') problems.push({ field: 'item', index });
  });
  return problems;
}

/** The PATCH body: only an action keeps its assignee and its due date. */
export function bodyOf(draft: Draft): UpdateMinutesDto {
  const location = draft.location.trim();
  return {
    template: draft.template,
    language: draft.language,
    title: draft.title.trim(),
    heldAt: instantOf(draft.day, draft.clock),
    location: location === '' ? null : location,
    attendees: draft.attendees.map((a) => ({
      userId: a.userId,
      name: a.name.trim(),
      organization: a.organization?.trim() ? a.organization.trim() : null,
      present: a.present,
    })),
    items: draft.items.map((item) => ({
      id: item.id,
      kind: item.kind,
      text: item.text.trim(),
      assigneeId: item.kind === 'action' ? item.assigneeId : null,
      dueDate: item.kind === 'action' ? item.dueDate : null,
    })),
  };
}

export function newItem(kind: ItemKind): DraftItem {
  return {
    id: crypto.randomUUID(),
    kind,
    text: '',
    assigneeId: null,
    assigneeName: null,
    dueDate: null,
    taskId: null,
    carriedFrom: null,
  };
}

/** Swaps an item with its neighbour of the same kind (the list is shown grouped by kind). */
export function moveItem(items: readonly DraftItem[], id: string, direction: -1 | 1): DraftItem[] {
  const from = items.findIndex((item) => item.id === id);
  const kind = items[from]?.kind;
  if (from < 0 || kind === undefined) return [...items];
  let to = from + direction;
  while (to >= 0 && to < items.length && items[to]?.kind !== kind) to += direction;
  if (to < 0 || to >= items.length) return [...items];
  const next = [...items];
  [next[from], next[to]] = [next[to] as DraftItem, next[from] as DraftItem];
  return next;
}

export const itemsOfKind = <T extends { kind: ItemKind }>(
  items: readonly T[],
  kind: ItemKind,
): T[] => items.filter((item) => item.kind === kind);

/** The PV is read in its own language: the direction of its text. */
export const directionOf = (language: MinutesLanguage): 'rtl' | 'ltr' =>
  language === 'ar' ? 'rtl' : 'ltr';

// --- Addresses.

export const meetingPath = (meetingId: string): string =>
  `/meetings/${encodeURIComponent(meetingId)}`;

// --- Server answers.

export const apiErrorCode = (error: unknown): string | null =>
  error instanceof ApiError ? error.code : null;
export const isStaleVersion = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 412 || error.code === 'STALE_VERSION');
export const isPdfNotReady = (error: unknown): boolean => apiErrorCode(error) === 'PDF_NOT_READY';

/** `items.2.assigneeId` → the index and the code of the refusal. */
export function itemErrorsOf(error: unknown): { index: number; code: string }[] {
  if (!(error instanceof ApiError)) return [];
  const found: { index: number; code: string }[] = [];
  for (const fieldError of error.fieldErrors) {
    const match = /^items\.(\d+)\./.exec(fieldError.path);
    if (match) found.push({ index: Number(match[1]), code: fieldError.code });
  }
  return found;
}

/** The PV the server says already exists for the meeting or event (409 MINUTES_EXIST). */
export function existingMinutesId(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== 'MINUTES_EXIST') return null;
  return error.problem?.minutesId ?? null;
}
